import { advtPerformance } from '@schibsted-nmp/advertising-events';
import { debugLog } from '@schibsted-nmp/advertising-shared';

import { loadTagsWithRelevantDigitalPrebid } from './relevantDigitalPrebid';
import { debounce } from '../utils/debounce';

// How rare Xandr /v3 requests are done to the https://acdn.adnxs.com/ast/ast.js library
const XANDR_REQUEST_DEBOUNCE = 200;

type TUsePrebid = 'relevant-digital' | 'pbjs' | 'none';

interface CallbackType {
  (arg?: any): void;
}

export function pushToAnqSafe(callback: CallbackType): void {
  if (window.apntag && window.apntag.anq) {
    window.apntag.anq.push(() => {
      if (window.apntag) {
        callback();
      }
    });
  }
}

export function setPageOpts(pageOpts: any, callback?: CallbackType): void {
  advtPerformance.markXandrEvents('setPageOpts');
  if (window?.apntag?.setPageOpts && pageOpts) {
    pushToAnqSafe(() => {
      window.apntag.setPageOpts(pageOpts);
      callback?.();
    });
  }
}

export function loadAstTags(usePrebid: TUsePrebid = 'none'): void {
  advtPerformance.markXandrEvents('loadTags');
  pushToAnqSafe(() => {
    if (usePrebid === 'relevant-digital') {
      loadTagsWithRelevantDigitalPrebid();
    } else {
      window.apntag.loadTags();
    }
  });
}

const debouncedLoadTagsFunction = debounce(loadAstTags, XANDR_REQUEST_DEBOUNCE);

export function debouncedLoadTags(usePrebid: TUsePrebid = 'none'): void {
  debouncedLoadTagsFunction(usePrebid);
}

export function defineTag(tag: any): void {
  advtPerformance.markXandrEvents(`defineTag - ${tag.targetId}`);
  if (tag && window.apntag) {
    debugLog(`defineTag `, tag);
    window.apntag.defineTag(tag);
  }
}

export function showTag(id: string): void {
  if (window.apntag) {
    debugLog(`showTag ${id} `);
    advtPerformance.markXandrEvents(`showTag - ${id}`);
    window.apntag.showTag(id);
  }
}

function setupEvent(event: string, id: string, callback?: CallbackType): void {
  debugLog(`SETUP ${event} for id: ${id}`);
  if (window.apntag) {
    window.apntag.onEvent(event, id, (adObj) => {
      if (window.apntag) {
        debugLog(`EVENT: ${event} for id: ${id}`);
        advtPerformance.markXandrEvents(`${event} - ${id}`);
        callback?.(adObj);
      }
    });
  }
}

export function onAdRequested(id: string, callback?: CallbackType): void {
  setupEvent('adRequested', id, callback);
}

export function onAdAvailable(id: string, callback?: CallbackType): void {
  setupEvent('adAvailable', id, callback);
}

export function onAdLoaded(id: string, callback?: CallbackType): void {
  setupEvent('adLoaded', id, callback);
}

export function onAdNoBid(id: string, callback?: CallbackType): void {
  setupEvent('adNoBid', id, callback);
}

export function onAdNoBidMediated(id: string, callback?: CallbackType): void {
  setupEvent('adNoBidMediated', id, callback);
}

export function onAdBadRequest(id: string, callback?: CallbackType): void {
  setupEvent('adBadRequest', id, callback);
}

interface Callbacks {
  onAdRequestedCallback?: CallbackType;
  onAdAvailableCallback?: CallbackType;
  onAdLoadedCallback?: CallbackType;
  onAdNoBidCallback?: CallbackType;
  onAdBadRequestCallback?: CallbackType;
}

// Used if you need all event listeners at one place with no specific logic
export function setupListeners(
  placementId: string,
  callbacks: Callbacks
): void {
  const events = [
    'adRequested',
    'adAvailable',
    'adLoaded',
    'adNoBid',
    'adBadRequest'
  ];

  events.forEach((event) => {
    setupEvent(event, placementId, callbacks[`${event}Callback`]);
  });
}
